import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';
import styled from 'styled-components';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useLanguageContext } from 'store/language';
import { usePermissionsContext } from 'store/permissions';

import ReactTableWrapper from 'components/table/ReactTableWrapper';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import { ISelectOptionProps } from 'components/form/types';
import Loader from 'components/Loader';
import { IDropdowns } from './types';

interface ICategoryData {
  id: number;
  name: string;
}

interface IGroupData {
  id: string;
  name: string;
  can_view_sensitive_fields: boolean;
  categories: any[];
}

interface IFormattedData {
  groups: Record<string, IGroupData>;
}

interface IProps {
  methods: any;
  btnLabel: string;
  title?: string;
  roleOptions: ISelectOptionProps[];
  userDataOptions: IDropdowns;
  isEdit?: boolean;
  client?: any;
  setIsOrganizeGroup?: (value: boolean) => void;
  handleSubmit: (data: any) => any;
}

type CodeTuple = [string, string];

const EmployeeOrganizeGroups: FC<IProps> = ({
  methods,
  btnLabel,
  title,
  roleOptions,
  userDataOptions,
  isEdit,
  client,
  setIsOrganizeGroup,
}: IProps) => {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { id: userId } = useParams<{ id: string | undefined }>();
  const { watch } = methods;
  const { showModal } = useModal();

  const [categories, setCategories] = useState<ICategoryData[]>([]);
  const [permissionsScope, setPermissionsScope] = useState<IFormattedData>();
  const [loading, setLoading] = useState<boolean>(false);

  // watch to enable re-render when employee type is changed
  const employeeType = watch('type') as ISelectOptionProps;
  const isEN = language === 'en';

  const handleSubmit = async () => {
    let formattedData: IFormattedData = { groups: {} };

    const groups = permissionsScope?.groups;
    if (groups && typeof groups === 'object') {
      formattedData = {
        groups: Object.entries(groups).reduce<Record<string, IGroupData>>((acc, [id, group]) => {
          acc[id] = {
            id,
            name: group.name,
            can_view_sensitive_fields: group.can_view_sensitive_fields,
            categories: group.categories.map(category => category.id),
          };
          return acc;
        }, {}),
      };
    }

    try {
      await API.post(`/permissions-scope/${userId}`, formattedData);
      showModal({
        type: 'success',
        content: t('structure.employees.editSuccessMsg'),
      });
      setIsOrganizeGroup?.(false);
    } catch (error) {
      showModal({
        type: 'error',
        content: createErrorFromResponse(error).message,
      });
      setIsOrganizeGroup?.(false);
    }
  };

  useEffect(() => {
    if (userId) {
      setLoading(true);
      const fetchPermissions = async () => {
        try {
          const permissionsScopeResponse = await API.get(`/permissions-scope/${userId}`);
          setPermissionsScope(permissionsScopeResponse.data);
          const categoriesResponse = await API.get(`/users?id=${userId}`);
          setCategories(categoriesResponse.data.items[0].category_permissions);
          setLoading(false);
        } catch (error) {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
          setLoading(false);
        }
      };

      fetchPermissions();
    }
  }, [userId]);

  const toggleCategory = (groupIndex: string, categoryId: number) => {
    setPermissionsScope(prevState => {
      // Deep clone the previous state to ensure immutability
      const updatedState = JSON.parse(JSON.stringify(prevState));

      const currentGroup = updatedState.groups[groupIndex];
      if (!currentGroup) {
        // If the group is not found, return the unchanged state
        return prevState;
      }

      const categoryExists = currentGroup.categories.some((cat: ICategoryData) => cat.id === categoryId);

      if (currentGroup.categories.length === 0) {
        // If categories array is empty and a category is clicked,
        // add all other categories except the clicked one.
        currentGroup.categories = categories
          .filter(cat => cat.id !== categoryId)
          .map(cat => ({
            id: cat.id,
            name: cat.name,
          }));
      } else if (categoryExists) {
        // If the category exists (and categories array is not empty),
        // remove the category.
        currentGroup.categories = currentGroup.categories.filter((cat: ICategoryData) => cat.id !== categoryId);
      } else {
        // If the category doesn't exist (and categories array is not empty),
        // add the category.
        currentGroup.categories.push({
          id: categoryId,
          name: categories.find(cat => cat.id === categoryId)?.name || '',
        });
      }

      return updatedState;
    });
  };

  const toggleCanViewSensitiveFields = (groupId: string) => {
    setPermissionsScope(prevState => {
      // Clone the previous state deeply to ensure immutability
      const updatedState = JSON.parse(JSON.stringify(prevState));

      const currentGroup = updatedState.groups[groupId];
      if (currentGroup) {
        // Toggle the value
        currentGroup.can_view_sensitive_fields = !currentGroup.can_view_sensitive_fields;
      }

      return updatedState;
    });
  };

  const generateColumns = (): Column<Record<string, unknown>>[] => {
    const baseColumns: Column<Record<string, unknown>>[] = [
      {
        Header: t('structure.groups.title') || 'Group Name',
        accessor: 'name',
        Cell: ({ value }: { value: any }) => {
          const cellStyle = { fontWeight: 600 };
          return <div style={cellStyle}>{value}</div>;
        },
      },
      {
        Header: t('structure.employees.accessToSensitiveData') || 'Can View Sensitive Fields',
        accessor: 'can_view_sensitive_fields',
        Cell: ({ row, value }: { row: Row<Record<string, unknown>>; value: any }) => {
          const cellStyle = value ? { color: '#28a745', cursor: 'pointer' } : { color: '#dc3545', cursor: 'pointer' };
          return (
            <div
              onClick={() => toggleCanViewSensitiveFields(row.original.id as string)}
              style={cellStyle}
            >
              {value ? t('common.yes') : t('common.no')}
            </div>
          );
        },
      },
    ];

    const categoryColumns = categories.map(category => ({
      Header: category.name || 'Category',
      accessor: `categories_${category.id}`,
      Cell: ({ row, value }: { row: Row<Record<string, unknown>>; value: any }) => {
        const cellStyle = value ? { color: '#28a745', cursor: 'pointer' } : { color: '#dc3545', cursor: 'pointer' };

        return (
          <div
            onClick={() => toggleCategory(row.original.id as string, category.id)}
            style={cellStyle}
          >
            {value ? t('common.yes') : t('common.no')}
          </div>
        );
      },
    }));

    return [...baseColumns, ...categoryColumns];
  };

  const prepareTableData = () => {
    return Object.entries(permissionsScope?.groups || {}).map(([groupId, group]) => {
      const categoryFlags: Record<string, boolean> = {};

      if (group.categories.length === 0) {
        // If the categories array is empty, set all category cells to true
        categories.forEach(category => {
          categoryFlags[`categories_${category.id}`] = true;
        });
      } else {
        // Otherwise, set specific category cells to true
        categories.forEach(category => {
          categoryFlags[`categories_${category.id}`] = group.categories.some(cat => cat.id === category.id);
        });
      }

      const groupData: IGroupData = {
        id: groupId,
        name: group.name,
        can_view_sensitive_fields: group.can_view_sensitive_fields,
        categories: group.categories.map(category => ({
          id: category.id,
          name: category.name,
        })),
      };

      return { ...groupData, ...categoryFlags };
    });
  };

  if (!isEdit || typeof employeeType?.value !== 'string' || !employeeType?.value.includes('moderator')) {
    return null;
  }

  const tableKey = JSON.stringify(permissionsScope);

  return (
    <>
      <BackLink onClick={() => setIsOrganizeGroup?.(false)}>{t('structure.employees.backToEdit')}</BackLink>
      <Form
        title={title}
        methods={methods}
        onSubmit={handleSubmit}
        btnLabel={btnLabel}
        showGlobalError
        isHorizontal
      >
        <FormGroup
          controlId="formGroupFirstName"
          label={t(`${isEN ? 'structure.employees.name_en' : 'structure.employees.name'}`)}
          type="text"
          name="first_name"
          isHorizontal
          isDisabled
        />
        <FormGroup
          controlId="formGroupMiddleName"
          label={t(`${isEN ? 'structure.employees.middleName_en' : 'structure.employees.middleName'}`)}
          type="text"
          name="middle_name"
          isHorizontal
          isDisabled
        />
        <FormGroup
          controlId="formGroupLastName"
          label={t(`${isEN ? 'structure.employees.lastName_en' : 'structure.employees.lastName'}`)}
          type="text"
          name="last_name"
          isHorizontal
          isDisabled
        />
        <FormGroup
          controlId="formGroupRoles"
          label={t('structure.employees.allRoles')}
          type="select"
          as="select"
          name="roles"
          isMulti
          isHorizontal
          allowSelectAll
          selectOptions={roleOptions}
          isDisabled
        />
        <hr />
        <div style={{ width: '100%', textAlign: 'center', marginBottom: '1rem' }}>
          <i>{t('structure.employees.rolesTableInfo')}</i>
        </div>
        {!loading ? (
          <ReactTableWrapper
            key={tableKey}
            data={prepareTableData()}
            columns={generateColumns()}
            hideFilterBar
          />
        ) : (
          <div className="w-100 d-flex justify-content-center align-items-center">
            <Loader />
          </div>
        )}
      </Form>
    </>
  );
};

export default EmployeeOrganizeGroups;

const BackLink = styled.div`
  cursor: pointer;
  font-weight: 400;
  font-size: 15px;
  margin-bottom: 1rem;
  color: #716aca;

  &:hover {
    color: #6f42c1;
  }
`;
